import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { can } from '../../marketing/model';
import type { MarketingBootstrap } from '../../marketing/types';
import { FkIcon } from '../Icons';
import { GUIDE_STEPS, TOUR_LABELS, TOUR_STEPS } from './onboardingContent';
import { tourStepAllowed, useOnboarding } from './OnboardingContext';

const WAIT_MS = 1500;
const POLL_MS = 150;

/** Открытые оверлеи antd: пока хоть один виден, Escape принадлежит ему, а не туру. */
const OVERLAY_SELECTOR = [
  '.ant-modal-wrap:not([style*="display: none"])',
  '.ant-drawer-open',
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
  '.ant-dropdown:not(.ant-dropdown-hidden)',
  // Открытое мобильное меню тоже закрывается по Escape — одно нажатие не должно делать два действия.
  '.fk-shell.fk-menu-open',
].join(', ');

function spotlightSelector(spotlight?: string): string | null {
  if (!spotlight) return null;
  return spotlight === 'flow' ? '.scenario-canvas' : `[data-tour="${spotlight}"]`;
}

/** Плавающая карточка тура (портал в body, z 50). Рендерится провайдером только когда tourStep !== null. */
export function TourCard({ bootstrap }: { bootstrap: MarketingBootstrap }) {
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const index = onboarding.tourStep ?? 0;
  const step = TOUR_STEPS[index];
  const total = TOUR_STEPS.length;
  const allowed = tourStepAllowed(bootstrap, index);
  const resume = onboarding.tourResume;

  // Подсветка цели: элемент может появиться позже (страница грузится) — ждём до 1,5 с.
  useLayoutEffect(() => {
    if (resume || !allowed) return;
    const selector = spotlightSelector(step?.spotlight);
    if (!selector) return;
    let target: HTMLElement | null = null;
    let waited = 0;
    const apply = () => {
      target = document.querySelector<HTMLElement>(selector);
      if (!target) return false;
      target.classList.add('mk-tour-spotlight');
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    };
    if (apply()) return () => target?.classList.remove('mk-tour-spotlight');
    const timer = window.setInterval(() => {
      waited += POLL_MS;
      if (apply() || waited >= WAIT_MS) window.clearInterval(timer);
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
      target?.classList.remove('mk-tour-spotlight');
    };
  }, [index, resume, allowed, step]);

  useEffect(() => {
    ctaRef.current?.focus({ preventScroll: true });
  }, [index, resume]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Escape в открытой модалке/дропдауне antd закрывает их, а не обучение.
      if (document.querySelector(OVERLAY_SELECTOR)) return;
      onboarding.skipTour();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onboarding]);

  if (!step) return null;
  const last = index === total - 1;
  const title = allowed ? step.title : (step.lockedTitle ?? step.title);
  const text = allowed
    ? step.key === 'editor-launch' && !can(bootstrap, 'launch')
      ? GUIDE_STEPS[2].noLaunchText ?? step.text
      : step.text
    : (step.lockedText ?? step.text);
  const secondary = step.secondary && (!step.secondary.requires || can(bootstrap, step.secondary.requires))
    ? step.secondary
    : null;

  return createPortal(
    <aside
      className="marketing-root fk-tour-card"
      role="dialog"
      aria-modal="false"
      aria-label="Обучение"
    >
      <div className="fk-tour-head">
        <img src="/brand/illus-gift.png" alt="" aria-hidden="true" />
        <div className="fk-kicker fk-on-dark">
          {resume ? TOUR_LABELS.resumeKicker : TOUR_LABELS.kicker(index + 1, total)}
        </div>
        <button
          type="button"
          className="fk-tour-close"
          aria-label={TOUR_LABELS.skipAria}
          onClick={onboarding.skipTour}
        >
          <FkIcon name="x" size={16} />
        </button>
      </div>
      {resume ? (
        <>
          <h2>{TOUR_LABELS.resumeTitle}</h2>
          <p aria-live="polite">{TOUR_LABELS.resumeText(index + 1, step.title)}</p>
        </>
      ) : (
        <>
          <h2>{title}</h2>
          <p aria-live="polite">{text}</p>
          {onboarding.tourNote && <small className="fk-tour-note">{onboarding.tourNote}</small>}
        </>
      )}
      <div className="fk-tour-dots" aria-hidden="true">
        {TOUR_STEPS.map((item, i) => (
          <i key={item.key} className={i <= index ? 'on' : ''} />
        ))}
      </div>
      <div className="fk-tour-foot">
        <button type="button" className="mk-button mk-text mk-on-dark" onClick={onboarding.skipTour}>
          {TOUR_LABELS.skip}
        </button>
        <div className="mk-row">
          {resume ? (
            <>
              <button
                ref={allowed ? undefined : ctaRef}
                type="button"
                className="mk-button mk-ghost-dark"
                onClick={onboarding.restartTour}
              >
                {TOUR_LABELS.restart}
              </button>
              {allowed && (
                <button ref={ctaRef} type="button" className="mk-button mk-primary" onClick={onboarding.resumeTour}>
                  {TOUR_LABELS.resume}
                </button>
              )}
            </>
          ) : (
            <>
              {index > 0 && (
                <button type="button" className="mk-button mk-ghost-dark" onClick={onboarding.prevTour}>
                  {TOUR_LABELS.back}
                </button>
              )}
              {secondary && (
                <button
                  type="button"
                  className="mk-button mk-ghost-dark"
                  onClick={() => {
                    onboarding.endTour();
                    navigate(secondary.to);
                  }}
                >
                  {secondary.label}
                </button>
              )}
              <button ref={ctaRef} type="button" className="mk-button mk-primary" onClick={onboarding.nextTour}>
                {step.cta ?? (last ? TOUR_LABELS.finish : index === 0 ? TOUR_LABELS.start : TOUR_LABELS.next)}
              </button>
            </>
          )}
        </div>
      </div>
    </aside>,
    document.body,
  );
}
